/**
 * What the inspector is currently about (research D9).
 *
 * Held in the URL rather than in component state so that a selection survives a
 * reload and can be linked to — the same reason the read-only graph page keeps
 * `?step=` there. One parameter rather than three, because the three kinds are
 * mutually exclusive and three parameters would make illegal combinations
 * representable.
 */
export type BuilderSelection =
  | { kind: 'step'; id: string }
  /** `null` means no selection, and the inspector shows workflow-level fields (FR-027). */
  | { kind: 'transition'; stepId: string; key: string }
  | { kind: 'phase'; id: string }
  | null;

/**
 * Serialise for the `sel` search parameter.
 *
 * `edge:` rather than `transition:` keeps the common case short in a URL a user
 * might actually look at.
 */
export function serialiseSelection(
  selection: BuilderSelection,
): string | undefined {
  if (!selection) return undefined;
  switch (selection.kind) {
    case 'step':
      return `step:${selection.id}`;
    case 'transition':
      return `edge:${selection.stepId}:${selection.key}`;
    case 'phase':
      return `phase:${selection.id}`;
  }
}

/**
 * Parse the `sel` parameter, returning `null` for anything unrecognised.
 *
 * Search parameters are user-editable text and may also be stale — pointing at
 * a step someone else deleted. Both degrade to "nothing selected" rather than
 * failing the route, which is what `WorkflowGraphPage` already does with
 * `?step=`. A URL is a suggestion about view state, not an assertion about
 * what exists.
 *
 * Validity against the *current* draft is deliberately not checked here: this
 * module is pure and has no draft. The caller resolves a parsed selection
 * against what it holds, and drops it if it no longer resolves.
 */
export function parseSelection(raw: string | undefined): BuilderSelection {
  if (!raw) return null;

  const [kind, ...rest] = raw.split(':');

  if (kind === 'step' && rest.length === 1 && rest[0]) {
    return { kind: 'step', id: rest[0] };
  }
  if (kind === 'phase' && rest.length === 1 && rest[0]) {
    return { kind: 'phase', id: rest[0] };
  }
  // A transition key may itself contain nothing exotic, but a step id cannot
  // contain a colon, so the first segment is the step and the remainder is the
  // key — rejoined rather than truncated.
  if (kind === 'edge' && rest.length >= 2 && rest[0] && rest[1]) {
    return {
      kind: 'transition',
      stepId: rest[0],
      key: rest.slice(1).join(':'),
    };
  }

  return null;
}

/**
 * The canvas id of a transition's edge: `stepId:key`.
 *
 * The format was written out at four unrelated sites — twice parsed in
 * `BuilderCanvas`, once compared against a selection there, and once minted in
 * `workflowToGraph` — so the canvas and the URL agreed only by coincidence. A
 * transition key may itself contain a colon, which is the detail each of those
 * sites had to remember independently: the split is on the *first* colon only.
 */
export function transitionEdgeId(stepId: string, key: string): string {
  return `${stepId}:${key}`;
}

/** The inverse of {@link transitionEdgeId}. */
export function parseTransitionEdgeId(edgeId: string): {
  stepId: string;
  key: string;
} {
  const at = edgeId.indexOf(':');
  if (at === -1) return { stepId: edgeId, key: '' };
  return { stepId: edgeId.slice(0, at), key: edgeId.slice(at + 1) };
}

/**
 * The canvas id of a phase's band: `phase:<id>`.
 *
 * Prefixed because a band and a step share one id space on the canvas, and a
 * phase may legitimately be called the same thing as a step. Given a name here
 * for the reason {@link transitionEdgeId} has one: the prefix was written out
 * at three call sites as a bare `replace(/^phase:/, '')`, so the canvas and the
 * view-model agreed about it only by coincidence.
 */
export function phaseNodeId(phaseId: string): string {
  return `phase:${phaseId}`;
}

/** The inverse of {@link phaseNodeId}, tolerant of an id that carries no prefix. */
export function parsePhaseNodeId(nodeId: string): string {
  return nodeId.startsWith('phase:') ? nodeId.slice('phase:'.length) : nodeId;
}

/**
 * The minimum a draft must provide for a selection to be resolved against it.
 *
 * Structural rather than `WorkflowDraft`, following `layout.ts`: this module
 * stays free of the draft type, so it can be tested with three lines of literal
 * instead of a whole document.
 */
export type SelectionScope = {
  phases: ReadonlyArray<{
    id: string;
    steps: ReadonlyArray<{
      id: string;
      transitions: ReadonlyArray<{ key: string }>;
    }>;
  }>;
};

/**
 * Drop a selection that no longer names anything in the document.
 *
 * A selection lives in the URL, so it outlives the thing it points at: delete a
 * step, follow a stale link, undo past a rename, and `?sel=` still says
 * `step:specify`. That is *stale* rather than wrong, and it degrades to nothing
 * selected — the inspector falls back to workflow-level fields rather than
 * showing a step that is gone.
 *
 * Separate from `parseSelection` on purpose: parsing is about the string and
 * needs no document, and keeping the two apart is what lets each be tested for
 * one thing.
 */
export function resolveSelection(
  scope: SelectionScope,
  parsed: BuilderSelection,
): BuilderSelection {
  if (!parsed) return null;

  const steps = scope.phases.flatMap((phase) => phase.steps);
  if (parsed.kind === 'step') {
    return steps.some((step) => step.id === parsed.id) ? parsed : null;
  }
  if (parsed.kind === 'phase') {
    return scope.phases.some((phase) => phase.id === parsed.id) ? parsed : null;
  }
  const owner = steps.find((step) => step.id === parsed.stepId);
  return owner?.transitions.some((t) => t.key === parsed.key) ? parsed : null;
}
